import { useEffect, useState } from 'react'
import type { ComponentKind } from '@kronos/domain'
import { imageUrl } from '../imageFile'
import { TreeIcon } from './treeIcons'

/**
 * The picture on a node: the item's photograph, or a tinted tile that says how
 * the thing comes to exist.
 *
 * Not `Thumb` from `Photos.tsx`, whose fallback is the name's first letter —
 * on a tree every placeholder begins with the same letter, and a column of
 * identical "P"s is noise where a picture should be. A tile with a box, a
 * factory or a spanner says something even before anybody has photographed
 * anything, which is the normal state of a new item master.
 */
export function NodeThumb({
  imageId,
  kind,
  consumed = false,
  root = false,
  size = 48,
}: {
  imageId: string | null
  kind: ComponentKind
  consumed?: boolean
  root?: boolean
  size?: number
}): JSX.Element {
  /* A photo whose bytes have gone (every dev-scenario load wipes them) falls
     back to the tile rather than to the browser's broken-image glyph. */
  const [broken, setBroken] = useState(false)
  useEffect(() => setBroken(false), [imageId])

  if (imageId !== null && !broken) {
    return (
      <img
        className="st-thumb"
        style={{ width: size, height: size }}
        src={imageUrl(imageId)}
        alt=""
        loading="lazy"
        draggable={false}
        onError={() => setBroken(true)}
      />
    )
  }
  const icon = root ? 'robot' : consumed ? 'consumed' : kind === 'buy' ? 'bought' : kind === 'make_external' ? 'vendor' : 'built'
  return (
    <span
      className={`st-thumb st-thumb--tile st-thumb--${root ? 'root' : consumed ? 'consumed' : kind}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <TreeIcon name={icon} size={Math.round(size * 0.46)} />
    </span>
  )
}
